import React, { useState, useEffect } from "react";
import { Box, Typography, makeStyles, Chip } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import InputSearch from "components/Input/InputSearch";
import { COMPLAINT_STATUS, COMPLAINT_SUBMITTER_TYPE } from "config/AppConfig";
import AppSelect from "components/Select/AppSelect";
import {
  fetchPageComplaint,
  getComplaintList,
  getComplaintPaging,
  getComplaintStats,
} from "store/slices/complaint";

const useStyles = makeStyles(() => ({
  container: {
    padding: "10px",
    backgroundColor: '#fff'
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "12px",
    marginBottom: "16px",
  },
  statCard: {
    borderRadius: "12px",
    padding: "16px 20px",
  },
  statNumber: {
    fontSize: "24px",
    fontWeight: 500,
    marginBottom: "2px",
  },
  statLabel: {
    fontSize: "12px",
  },
  filtersRow: {
    display: "flex",
    gap: "8px",
    marginBottom: "16px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  tableCard: {
    background: "#fff",
    borderRadius: "12px",
    border: "0.5px solid #e5e5e2",
    overflow: "hidden",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: "10px 8px",
    fontSize: "11px",
    fontWeight: 500,
    color: "#888780",
    borderBottom: "0.5px solid #e5e5e2",
    background: "#F5F5F3",
    "&:first-child": {
      paddingLeft: "16px",
    },
  },
  td: {
    padding: "12px 8px",
    color: "#1a1a18",
    borderBottom: "0.5px solid #F1EFE8",
    "&:first-child": {
      paddingLeft: "16px",
    },
  },
  overdueRow: {
    background: "#FFF8F8",
  },
  paginationRow: {
    padding: "12px 16px",
    borderTop: "0.5px solid #e5e5e2",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#F5F5F3",
  },
  badgeRed: {
    background: "#FCEBEB",
    color: "#A32D2D",
  },
  badgeGreen: {
    background: "#EAF3DE",
    color: "#3B6D11",
  },
  badgeAmber: {
    background: "#FAEEDA",
    color: "#854F0B",
  },
  badgeBlue: {
    background: "#E6F1FB",
    color: "#185FA5",
  },
  badgeTeal: {
    background: "#E1F5EE",
    color: "#0F6E56",
  },
  slaOverdue: {
    color: "#A32D2D",
    fontWeight: 500,
    fontSize: "12px",
  },
  slaWarning: {
    color: "#854F0B",
    fontWeight: 500,
    fontSize: "12px",
  },
  slaOk: {
    color: "#3B6D11",
    fontWeight: 500,
    fontSize: "12px",
  },
}));

const ComplaintTable = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const complaints = useSelector(getComplaintList);
  const { totalElements, totalPages } = useSelector(getComplaintPaging);
  const stats = useSelector(getComplaintStats);

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedSubmitter, setSelectedSubmitter] = useState(null);
  const [searchKey, setSearchKey] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(fetchPageComplaint({
      page: currentPage,
      status: selectedStatus,
      submitterType: selectedSubmitter,
      search: searchKey,
    }));
  }, [dispatch, currentPage, selectedStatus, selectedSubmitter, searchKey]);

  const handleSearch = (value) => {
    setSearchKey(value);
    setCurrentPage(1);
  };

  const statusOptions = [
    { value: null, label: t('label.complaint.all_status') },
    { value: COMPLAINT_STATUS.RECEIVED, label: t('label.complaint.step_received') || t('label.complaint.open') },
    { value: COMPLAINT_STATUS.UNDER_REVIEW, label: t('label.complaint.under_review') },
    { value: COMPLAINT_STATUS.OUT_COME_DECIDED, label: t('label.complaint.step_outcome_decided') || t('label.complaint.resolved') },
    { value: COMPLAINT_STATUS.CLOSED, label: t('label.complaint.step_closed') },
  ];

  const submitterOptions = [
    { value: null, label: t('label.complaint.all_submitters') },
    { value: COMPLAINT_SUBMITTER_TYPE.PARTICIPANT, label: t('label.participant') },
    { value: COMPLAINT_SUBMITTER_TYPE.FAMILY_GUARDIAN, label: t('label.complaint.family_guardian') },
    { value: COMPLAINT_SUBMITTER_TYPE.WORKER, label: t('label.complaint.worker') },
    { value: COMPLAINT_SUBMITTER_TYPE.OTHER, label: t('label.other') },
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  };

  const getSlaInfo = (complaint) => {
    if (complaint.status === COMPLAINT_STATUS.OUT_COME_DECIDED || complaint.status === COMPLAINT_STATUS.CLOSED) {
      return { text: t('label.complaint.step_outcome_decided') || t('label.complaint.resolved'), status: "ok" };
    }
    if (complaint.slaOverdue) {
      return { text: t('label.complaint.overdue_days', { days: complaint.slaOverdueDays || "" }).trim(), status: "overdue" };
    }
    if (complaint.slaDaysRemaining !== undefined) {
      const days = complaint.slaDaysRemaining;
      return {
        text: t('label.complaint.days_remaining', { days }),
        status: days <= 3 ? "warning" : "ok",
      };
    }
    return { text: t('label.complaint.no_sla'), status: "ok" };
  };

  const getRefLabel = (complaint) => {
    if (complaint.complaintNumber) return complaint.complaintNumber;
    return complaint.id ? `CMP-${String(complaint.id).padStart(2, "0")}` : "";
  };

  const getRelationshipLabel = (relationship) => {
    switch (relationship) {
      case "SUPPORT_WORKER":
        return t('label.complaint.relationship_support_worker');
      case "TEAM_LEADER_COORDINATOR":
        return t('label.complaint.relationship_team_leader_coordinator');
      case "ANOTHER_PARTICIPANT":
        return t('label.complaint.relationship_another_participant');
      case "ANOTHER_WORKER":
        return t('label.complaint.relationship_another_worker');
      case "ORGANISATION_SERVICE_GENERALLY":
        return t('label.complaint.relationship_organisation_service_generally');
      default:
        return relationship || "";
    }
  };

  const handleRowClick = (complaint) => {
    history.push(`/admin/complaints/${complaint.id}`);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case COMPLAINT_STATUS.RECEIVED:
        return classes.badgeRed;
      case COMPLAINT_STATUS.UNDER_REVIEW:
        return classes.badgeBlue;
      case COMPLAINT_STATUS.OUT_COME_DECIDED:
        return classes.badgeGreen;
      case COMPLAINT_STATUS.CLOSED:
      default:
        return classes.badgeAmber;
    }
  };

  const getSubmitterBadgeClass = (submitterType) => {
    switch (submitterType) {
      case COMPLAINT_SUBMITTER_TYPE.PARTICIPANT:
        return classes.badgeTeal;
      case COMPLAINT_SUBMITTER_TYPE.FAMILY_GUARDIAN:
        return classes.badgeAmber;
      case COMPLAINT_SUBMITTER_TYPE.WORKER:
        return classes.badgeBlue;
      default:
        return classes.badgeAmber;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case COMPLAINT_STATUS.RECEIVED:
        return t('label.complaint.step_received') || t('label.complaint.open');
      case COMPLAINT_STATUS.UNDER_REVIEW:
        return t('label.complaint.under_review');
      case COMPLAINT_STATUS.OUT_COME_DECIDED:
        return t('label.complaint.step_outcome_decided') || t('label.complaint.resolved');
      case COMPLAINT_STATUS.CLOSED:
        return t('label.complaint.step_closed') || "Closed";
      default:
        return status;
    }
  };

  const getSubmitterLabel = (submitterType) => {
    switch (submitterType) {
      case COMPLAINT_SUBMITTER_TYPE.PARTICIPANT:
        return t('label.participant');
      case COMPLAINT_SUBMITTER_TYPE.FAMILY_GUARDIAN:
        return t('label.complaint.family');
      case COMPLAINT_SUBMITTER_TYPE.WORKER:
        return t('label.complaint.worker');
      default:
        return t('label.other');
    }
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.headerRow}>
        <Typography style={{ fontSize: "20px", fontWeight: 500 }}>
          {t('title.complaints')}
        </Typography>
        <AppButton
          label={t('label.complaint.new')}
          buttonColor={AppColor.mainColor}
          rounder
          startIcon={<i className="fas fa-plus" style={{ fontSize: 14 }} />}
          onClick={() => { }}
        />
      </Box>

      <Box className={classes.statsGrid}>
        <Box
          className={classes.statCard}
          style={{ background: "#FCEBEB" }}
        >
          <Typography
            className={classes.statNumber}
            style={{ color: "#A32D2D" }}
          >
            {stats.overdue || 0}
          </Typography>
          <Typography
            className={classes.statLabel}
            style={{ color: "#A32D2D" }}
          >
            {t('label.complaint.overdue_stat_label')}
          </Typography>
        </Box>
        <Box
          className={classes.statCard}
          style={{ background: "#FAEEDA" }}
        >
          <Typography
            className={classes.statNumber}
            style={{ color: "#854F0B" }}
          >
            {stats.open || 0}
          </Typography>
          <Typography
            className={classes.statLabel}
            style={{ color: "#854F0B" }}
          >
            {t('label.complaint.open_stat_label')}
          </Typography>
        </Box>
        <Box
          className={classes.statCard}
          style={{ background: "#E6F1FB" }}
        >
          <Typography
            className={classes.statNumber}
            style={{ color: "#185FA5" }}
          >
            {stats.underReview || 0}
          </Typography>
          <Typography
            className={classes.statLabel}
            style={{ color: "#185FA5" }}
          >
            {t('label.complaint.under_review_stat_label')}
          </Typography>
        </Box>
        <Box
          className={classes.statCard}
          style={{ background: "#EAF3DE" }}
        >
          <Typography
            className={classes.statNumber}
            style={{ color: "#3B6D11" }}
          >
            {stats.resolved || 0}
          </Typography>
          <Typography
            className={classes.statLabel}
            style={{ color: "#3B6D11" }}
          >
            {t('label.complaint.resolved_stat_label')}
          </Typography>
        </Box>
      </Box>

      <Box className={classes.filtersRow}>
        <Box style={{ flex: 1, minWidth: "200px" }}>
          <InputSearch
            onSearch={handleSearch}
            searchText={searchKey}
            containerStyle={{ height: '41px' }}
          />
        </Box>
        <Box style={{ minWidth: "150px" }}>
          <AppSelect
            name="statusFilter"
            placeholder={t('label.status')}
            value={selectedStatus}
            isNotMargin={true}
            options={statusOptions}
            onChange={option => {
              setSelectedStatus(option.value);
              setCurrentPage(1);
            }}
          />
        </Box>
        <Box style={{ minWidth: "180px" }}>
          <AppSelect
            name="submitterFilter"
            placeholder={t('label.submitter')}
            value={selectedSubmitter}
            isNotMargin={true}
            options={submitterOptions}
            onChange={option => {
              setSelectedSubmitter(option.value);
              setCurrentPage(1);
            }}
          />
        </Box>
      </Box>

      <Box className={classes.tableCard}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th style={{ width: "150px" }} className={classes.th}>{t('label.complaint.ref')}</th>
              <th style={{ width: "70px" }} className={classes.th}>{t('label.date')}</th>
              <th style={{ width: "250px" }} className={classes.th}>{t('label.complaint.subject')}</th>
              <th style={{ width: "110px" }} className={classes.th}>{t('label.submitter')}</th>
              <th style={{ width: "130px" }} className={classes.th}>{t('label.relationship')}</th>
              <th style={{ width: "110px" }} className={classes.th}>{t('label.complaint.sla')}</th>
              <th style={{ width: "110px" }} className={classes.th}>{t('label.status')}</th>
              <th style={{ width: "80px" }} className={classes.th}></th>
            </tr>
          </thead>
          <tbody>
            {complaints && complaints.length > 0 ? (
              complaints.map((complaint) => {
                const slaInfo = getSlaInfo(complaint);
                return (
                  <tr
                    key={complaint.id}
                    className={slaInfo.status === "overdue" ? classes.overdueRow : ""}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleRowClick(complaint)}
                  >
                    <td className={classes.td} style={{ fontSize: "12px", fontWeight: 500, color: "#888780" }}>
                      {getRefLabel(complaint)}
                    </td>
                    <td className={classes.td} style={{ color: "#888780" }}>
                      {formatDate(complaint.createdAt)}
                    </td>
                    <td className={classes.td} style={{ fontWeight: 500 }}>
                      {complaint.complaintAbout || ""}
                    </td>
                    <td className={classes.td}>
                      <Chip
                        size="small"
                        label={getSubmitterLabel(complaint.complaintBy)}
                        className={getSubmitterBadgeClass(complaint.complaintBy)}
                      />
                    </td>
                    <td className={classes.td} style={{ color: "#5f5e5a" }}>
                      {getRelationshipLabel(complaint.complaintRelationship)}
                    </td>
                    <td className={classes.td}>
                      <span
                        className={
                          slaInfo.status === "overdue"
                            ? classes.slaOverdue
                            : slaInfo.status === "warning"
                              ? classes.slaWarning
                              : classes.slaOk
                        }
                      >
                        {slaInfo.text}
                      </span>
                    </td>
                    <td className={classes.td}>
                      <Chip
                        size="small"
                        label={getStatusLabel(complaint.status)}
                        className={getStatusBadgeClass(complaint.status)}
                      />
                    </td>
                    <td className={classes.td} align="right">
                      <AppButton
                        label={complaint.status === COMPLAINT_STATUS.RECEIVED ? t('label.review') : t('button.view')}
                        buttonColor={complaint.status === COMPLAINT_STATUS.RECEIVED ? AppColor.mainColor : "#F3F4F6"}
                        rounder
                        style={{
                          fontSize: 11,
                          height: 32,
                          minHeight: 32,
                          padding: "0 12px",
                          color: complaint.status === COMPLAINT_STATUS.RECEIVED ? "#fff" : "#374151",
                          border: complaint.status === COMPLAINT_STATUS.RECEIVED ? "none" : "1px solid #e5e7eb",
                          background: complaint.status === COMPLAINT_STATUS.RECEIVED ? AppColor.mainColor : "#fff",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowClick(complaint);
                        }}
                      />
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: "24px", color: "#888780" }}>
                  {t('label.complaint.no_complaints_found')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <Box className={classes.paginationRow}>
          <Typography style={{ fontSize: "12px", color: "#888780" }}>
            {t('label.complaint.showing', { shown: complaints ? complaints.length : 0, total: totalElements })}
          </Typography>
          <Box style={{ display: "flex", gap: "6px" }}>
            <AppButton
              label={t('label.complaint.previous')}
              buttonColor="#F3F4F6"
              rounder
              disabled={currentPage <= 1}
              style={{ fontSize: 11, height: 32, minHeight: 32, color: "#374151" }}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            />
            <AppButton
              label={t('button.next')}
              buttonColor="#F3F4F6"
              rounder
              disabled={currentPage >= totalPages}
              style={{ fontSize: 11, height: 32, minHeight: 32, color: "#374151" }}
              onClick={() => setCurrentPage((p) => p + 1)}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ComplaintTable;
